import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  TextField,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Alert,
  Collapse,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Modal,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  addDocumentWithNameDesc,
  getApplicationFromId,
} from '../services/ShareableApplicationService';
import Logo from '../components/Logo';
import CustomDocModal from '../components/shareableapplication/CustomDocModal';
import MaterialsTable from '../components/shareableapplication/MaterialsTable';
import DocumentsTable from '../components/shareableapplication/DocumentsTable';
import SitePhotosUploads from '../components/shareableapplication/SitePhotosUpload';
import PaymentTermsTable from '../components/shareableapplication/PaymentTermsTable';
import CloseIcon from '@mui/icons-material/Close';
import SignatureCanvas from 'react-signature-canvas';
import HelpIcon from '@mui/icons-material/Help';
import SignWithPAN from '../components/shareableapplication/SignWithPAN';
import ProjectTracking from '../components/shareableapplication/ProjectTracking';

const CUSTOM_DOCS = 'CUSTOM DOCS';
const PANEL_SERIAL_NUMBER = 'PANEL SERIAL NUMBER';
const INVERTER_SERIAL_NUMBER = 'INVERTER SERIAL NUMBER';
const REQUIRED_DOC_TYPES = [
  'SITE PHOTOS',
  'CHEQUE',
  'ELECTRICITY BILL',
  'IDENTITY',
  PANEL_SERIAL_NUMBER,
  INVERTER_SERIAL_NUMBER,
];
const ShareableApplication = () => {
  const { encodedId } = useParams();
  const [decodedId, setDecodedId] = useState(null);
  const [passkey, setPasskey] = useState('');
  const [application, setApplication] = useState(null);
  const [applicationDocuments, setApplicationDocuments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCustomDocModal, setShowCustomDocModal] = useState(false);
  const [signature, setSignature] = useState();
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signAlert, setSignAlert] = useState(false);
  const [isCertified, setIsCertified] = useState(false);
  const [panNumber, setPanNumber] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const documentTypes = applicationDocuments?.map(
    (doc) => doc?.document?.documentType,
  );
  const missingRequiredDocs = REQUIRED_DOC_TYPES.filter(
    (type) => !documentTypes?.includes(type),
  );

  const notAddedCount = missingRequiredDocs.length;

  const submitPAN = (value) => {
    setPanNumber(value);
  };

  useEffect(() => {
    try {
      const id = encodedId;
      setDecodedId(id);
    } catch (err) {
      setError('Invalid link.');
    }
  }, [encodedId]);

  const [pan, setPan] = useState('');

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getApplicationFromId(decodedId, passkey);
      setApplication(response.application);
      setApplicationDocuments(response.applicationDocuments);
      if (response === null) {
        setError('Invalid passkey or application not found.');
      }
    } catch (err) {
      setError('Invalid passkey or application not found.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedPassword = localStorage.getItem('myPasskey');
    if (savedPassword && decodedId) {
      console.log('savedPassword', savedPassword);
      setPasskey(savedPassword);
      handleSubmitWithMyPasskey(savedPassword);
    }
  }, [decodedId]);

  const handleSubmitWithMyPasskey = async (passkey) => {
    setLoading(true);
    setError('');

    try {
      const response = await getApplicationFromId(decodedId, passkey);
      setApplication(response.application);
      setApplicationDocuments(response.applicationDocuments);
      if (response === null) {
        setError('Invalid passkey or application not found.');
      }
    } catch (err) {
      setError('Invalid passkey or application not found.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSignature = async () => {
    if (!signature || signature.isEmpty()) {
      setError('Please add your signature before uploading.');
      return;
    }

    const signatureUrl = signature.toDataURL('image/png');
    const dataURLtoFile = (dataurl, filename) => {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    };

    const signatureFile = dataURLtoFile(signatureUrl, 'signature.png');
    const date = new Date();
    setLoading(true);
    try {
      const response = await addDocumentWithNameDesc(
        signatureFile,
        decodedId,
        CUSTOM_DOCS,
        'Signature Document',
        application?.consumerDetails?.name +
          ' | ' +
          application?.consumerDetails?.phone +
          ' | ' +
          date.toISOString() +
          ' |  ' +
          panNumber,
        'Signature uploaded',
      );
      setShowSignatureDialog(false);
      handleSubmit();
      alert('Signature uploaded successfully!');
    } catch (err) {
      setError('Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadWithDetails = async (
    event,
    docType,
    name,
    description,
    comment,
  ) => {
    const file = event.target.files[0];
    if (!file) {
      setError('Please select a file.');
      return;
    }

    setLoading(true);
    try {
      const respone = await addDocumentWithNameDesc(
        file,
        decodedId,
        docType,
        name,
        description,
        comment,
      );
      handleSubmit();
      alert(`${docType} uploaded successfully!`);
    } catch (err) {
      setError('Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  const [selectedTab, setSelectedTab] = useState('Project tracking');

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  const handleHelp = () => {
    window.open(
      'https://support.vaysolar.com/hc/en-us/requests/new?ticket_form_id=14570268863260',
      '_blank',
    );
  };
  function ChildModal() {
    return (
      <>
        <Button
          disabled={panNumber === ''}
          onClick={() => {
            setShowSignatureDialog(true);
            setSignAlert(true);
            handleClose();
          }}
          fullWidth
          variant='contained'
        >
          sign with pan
        </Button>
      </>
    );
  }
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: '90%', mx: 'auto' }} mt={2}>
      <Stack
        direction='row'
        justifyContent='center'
        alignItems='center'
        width='100%'
        position='relative'
      >
        <Logo />
        {application && (
          <Chip
            label='Need Help'
            component='a'
            href='https://forms.gle/FYBfYapmfg77pZkz9'
            target='_blank'
            clickable
            icon={<HelpIcon />}
            sx={{
              color: 'text.primary',
              textDecoration: 'none',
              position: 'absolute',
              right: isMobile ? 0 : 16,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
        )}
      </Stack>

      {decodedId !== null && !application ? (
        <>
          <Stack
            direction='row'
            justifyContent='center'
            width={isMobile ? '100%' : '50%'}
          >
            <TextField
              label='Enter Passkey to view details'
              variant='outlined'
              value={passkey}
              onChange={(e) => setPasskey(e.target.value)}
              fullWidth
            />
          </Stack>
          <Stack direction={'row'} spacing={2} mt={2}>
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit'}
            </Button>
            <Button variant='outlined' disabled={loading} onClick={handleHelp}>
              {'Help'}
            </Button>
          </Stack>
        </>
      ) : (
        <></>
      )}

      {error && <Typography color='error'>{error}</Typography>}
      {application && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
            <Typography variant={isMobile ? 'h4' : 'h3'} fontWeight={600}>
              {application?.consumerDetails?.name?.charAt(0).toUpperCase() +
                application?.consumerDetails?.name?.slice(1)}
              ’s {application?.plantInfo?.plantSize}
              {' kw '}
              {application?.plantInfo?.plantType?.toLowerCase()} solar
            </Typography>
            <Typography variant='body1' color='textSecondary'>
              {application?.consumerDetails?.phone}
              {application?.consumerDetails?.email && ', '}
              {application?.consumerDetails?.email}
              <br />
              {application?.connectionDetails?.discom}
            </Typography>
            <Typography variant='body1' color='textSecondary'>
              {application?.connectionDetails?.sanctionedLoad} {' kW '} load{' '}
              {application?.connectionDetails?.type?.toLowerCase()}
            </Typography>
            <Typography variant='body1' color='textSecondary'>
              {application?.connectionDetails?.category
                ?.charAt(0)
                ?.toUpperCase() +
                application?.connectionDetails?.category
                  ?.slice(1)
                  ?.toLowerCase()}{' '}
              CA number {application?.connectionDetails?.caNumber}
            </Typography>
            <Typography variant='body1' color='textSecondary'>
              {application?.propertyDetails?.address},{' '}
              {application?.propertyDetails?.district},{' '}
              {application?.propertyDetails?.state},{' '}
              {application?.propertyDetails?.pincode}
            </Typography>

            <Stack display={'flex'} mt={2} spacing={1} direction={'row'}>
              <Chip
                variant={
                  selectedTab === 'Project tracking' ? 'filled' : 'outlined'
                }
                label='Project tracking'
                clickable
                sx={{ fontWeight: 600, borderRadius: '16px' }}
                onClick={() => handleTabChange('Project tracking')}
              />
              <Chip
                variant={selectedTab === 'Documents' ? 'filled' : 'outlined'}
                label='Documents'
                clickable
                sx={{ fontWeight: 600, borderRadius: '16px' }}
                onClick={() => handleTabChange('Documents')}
              />
              <Chip
                variant={selectedTab === 'Materials' ? 'filled' : 'outlined'}
                label='Materials'
                clickable
                sx={{ fontWeight: 600, borderRadius: '16px' }}
                onClick={() => handleTabChange('Materials')}
              />
              <Chip
                variant={selectedTab === 'Site Photos' ? 'filled' : 'outlined'}
                label='Site Photos'
                clickable
                sx={{ fontWeight: 600, borderRadius: '16px' }}
                onClick={() => handleTabChange('Site Photos')}
              />
              {/* <Chip
                
                variant={selectedTab === 'Installer' ? 'filled' : 'outlined'}
                label='Installer'
                clickable
                sx={{ fontWeight: 600, borderRadius: '16px' }}
                onClick={() => handleTabChange('Installer')}
              /> */}
              <Chip
                variant={selectedTab === 'Pricing' ? 'filled' : 'outlined'}
                label='Pricing'
                clickable
                sx={{ fontWeight: 600, borderRadius: '16px' }}
                onClick={() => handleTabChange('Pricing')}
              />
            </Stack>
          </Box>

          <Box
            textAlign='right'
            display={'flex'}
            flexDirection={'column'}
            alignItems={'flex-end'}
            gap={'4px'}
          >
            <Typography variant='body1' fontWeight={600}>
              {application?.plantInfo?.installerName && 'by'}{' '}
              {application?.plantInfo?.installerName?.charAt(0).toUpperCase() +
                application?.plantInfo?.installerName?.slice(1)}
            </Typography>
            <Typography variant='body1' color='textSecondary'>
              {application?.plantInfo?.installerOffice}
            </Typography>
          </Box>
        </Box>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='parent-modal-title'
        aria-describedby='parent-modal-description'
      >
        <Box sx={{ ...style, width: 400 }}>
          <SignWithPAN pan={pan} setPan={setPan} panNumber={submitPAN} />
          <ChildModal />
        </Box>
      </Modal>
      {application && (
        <Box display={'flex'} mt={3} pb={6}>
          {selectedTab === 'Documents' && (
            <Stack spacing={2} width={isMobile ? '100%' : '600px'}>
              <Stack direction={'row'} justifyContent={'space-between'}>
                <Typography variant='h5' fontWeight={600}>
                  Documents{' '}
                  <Typography fontWeight={400} display='inline'>
                    {notAddedCount && `(${notAddedCount} left )`}
                  </Typography>
                </Typography>
                <Stack direction={'row'} spacing={2}>
                  <Stack direction='row' spacing={2} alignItems='center'>
                    <Button
                      variant='contained'
                      onClick={() => setShowCustomDocModal(true)}
                    >
                      Add document
                    </Button>

                    {showCustomDocModal && (
                      <CustomDocModal
                        onClose={() => setShowCustomDocModal(false)}
                        onUpload={(event, name, description, comment) =>
                          handleUploadWithDetails(
                            event,
                            CUSTOM_DOCS,
                            name,
                            description,
                            comment,
                          )
                        }
                      />
                    )}
                  </Stack>
                </Stack>
              </Stack>
              <DocumentsTable
                documents={applicationDocuments}
                decodedId={decodedId}
                passkey={passkey}
              />
              <Stack direction='row' spacing={2} justifyContent={'flex-end'}>
                <Button variant='contained' onClick={handleOpen}>
                  Add e-signature
                </Button>
              </Stack>
            </Stack>
          )}
          {selectedTab === 'Materials' && (
            <>
              <MaterialsTable materials={application.materials} />
            </>
          )}
          {selectedTab === 'Site Photos' && (
            <>
              <SitePhotosUploads
                documents={applicationDocuments}
                decodedId={decodedId}
                passkey={passkey}
                onDocumentsUpdate={handleSubmit}
              />
            </>
          )}
          {/* {selectedTab === 'Installer' && (
            <>
              <Typography variant='body1'>
                Installer content goes here...
              </Typography>
            </>
          )} */}
          {selectedTab === 'Pricing' && (
            <>
              <PaymentTermsTable
                paymentTerms={application.paymentTerms}
                pricing={application.pricing}
              />
            </>
          )}
          {selectedTab === 'Project tracking' && (
            <>
              <ProjectTracking
                projectTrackingData={application.projectTracking}
              />
            </>
          )}
        </Box>
      )}
      <Dialog
        open={showSignatureDialog}
        onClose={() => setShowSignatureDialog(false)}
      >
        <Collapse in={signAlert}>
          <Alert
            action={
              <IconButton
                aria-label='close'
                color='inherit'
                size='small'
                onClick={() => {
                  setSignAlert(false);
                }}
              >
                <CloseIcon fontSize='inherit' />
              </IconButton>
            }
          >
            Please sign in the box below
          </Alert>
        </Collapse>
        <DialogTitle>
          <Stack
            direction='row'
            justifyContent={'space-between'}
            alignItems='flex-end'
          >
            <Stack>
              <Typography fontWeight={600}>
                {application?.consumerDetails?.name}'s Signature
              </Typography>
              <Typography color='textSecondary'>
                ({application?.consumerDetails?.email})
              </Typography>
              <Typography color='textSecondary'>
                {application?.consumerDetails?.phone}
              </Typography>
            </Stack>
            <Button onClick={() => signature.clear()}>Clear</Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <SignatureCanvas
            penColor='#00008B'
            canvasProps={{
              width: 500,
              height: 200,
              className: 'sigCanvas',
              style: { border: '1px solid black' },
            }}
            ref={(ref) => {
              setSignature(ref);
            }}
          />
          <Stack direction='row' spacing={1} pt={2} alignItems='flex-start'>
            <Checkbox
              checked={isCertified}
              sx={{ padding: '1px' }}
              onChange={(e) => setIsCertified(e.target.checked)}
            />
            <Typography>
              I certify that this is my ({application?.consumerDetails?.name}
              's) true signature and can be verified using PAN
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSignatureDialog(false)}>Cancel</Button>

          <Button
            onClick={handleUploadSignature}
            variant='contained'
            disabled={!isCertified}
            title={
              !isCertified
                ? 'Self certification mandatory to add signature'
                : ''
            }
          >
            Upload Signature
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ShareableApplication;
